import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supportService } from '../../../services/support/supportService';
import { useSupportIdentity, useSupportUnread } from '../../../stores/appSupportStore';
import { SupportDrawer } from '../support/SupportDrawer';
import { useSupportDraftGuard } from '../support/useSupportDraftGuard';
import { setupSecondary } from './SetupPrimitives';

interface SetupSupportActionsContext { openSupport: (preferUnread?: boolean) => void; openConversations: () => void }
const SetupSupportContext = createContext<SetupSupportActionsContext>({ openSupport: () => {}, openConversations: () => {} });
export const useSetupSupport = () => useContext(SetupSupportContext);

export function SetupSupportActions() {
  const { t } = useTranslation();
  const { openSupport, openConversations } = useSetupSupport();
  return <>
    <button type="button" className={setupSecondary} onClick={() => openSupport()}>{t('accountSetup.help.contact')}</button>
    <button type="button" className={setupSecondary} onClick={openConversations}>{t('accountSetup.help.conversations')}</button>
  </>;
}

export default function SetupSupport({ restaurants, restaurantId, children }: {
  restaurants: { id: string; name: string }[]; restaurantId?: string; children?: ReactNode;
}) {
  const identity = useSupportIdentity();
  return identity ? <SetupSupportSession key={identity} restaurants={restaurants} restaurantId={restaurantId}>{children}</SetupSupportSession> : <>{children}</>;
}

function SetupSupportSession({ restaurants, restaurantId, children }: {
  restaurants: { id: string; name: string }[]; restaurantId?: string; children?: ReactNode;
}) {
  const { t } = useTranslation();
  const unread = useSupportUnread(true);
  const [params, setParams] = useSearchParams();
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(false);
  const threadId = params.get('thread');
  const view = threadId || params.get('view') === 'submitted' ? 'submitted' : params.get('view') === 'new' ? 'new' : null;
  const drawer: { view: 'new' | 'submitted'; threadId: string | null } | null = view ? { view, threadId } : null;
  const dirty = useRef(false);
  const activeRestaurant = useRef(restaurantId); activeRestaurant.current = restaurantId;
  const openingRef = useRef(false);
  const controller = useRef(new AbortController());
  useEffect(() => { controller.current = new AbortController(); return () => controller.current.abort(); }, []);
  useSupportDraftGuard(dirty, t('support:discardDraft'));
  const canLeave = () => !dirty.current || window.confirm(t('support:discardDraft'));
  const navigate = (view: 'new' | 'submitted', id?: string) => {
    if (drawer?.view === view && drawer.threadId === (id ?? null)) return;
    if (!canLeave()) return;
    dirty.current = false;
    const next = new URLSearchParams(params);
    next.set('view', view); next.delete('thread'); next.delete('type');
    if (id) next.set('thread', id);
    setParams(next);
  };
  const openSupport = async (preferUnread = false) => {
    // Keep an already open composer/transcript (and its draft) when another step asks for help.
    if (drawer || openingRef.current) return;
    openingRef.current = true; setOpening(true); setError(false);
    const requestedRestaurant = restaurantId;
    try {
      const filters = { setupOnly: true, restaurantId, limit: 1 };
      const signal = controller.current.signal;
      let page = await supportService.listThreads({ ...filters, ...(preferUnread ? { unreadOnly: true } : {}) }, false, signal);
      // The floating badge counts all visible conversations, so it must open an unread
      // thread even if that message belongs to another restaurant or a legacy thread.
      if (preferUnread && !page.threads.length) page = await supportService.listThreads({ unreadOnly: true, limit: 1 }, false, signal);
      if (preferUnread && !page.threads.length) page = await supportService.listThreads(filters, false, signal);
      if (signal.aborted || activeRestaurant.current !== requestedRestaurant) return;
      navigate(page.threads.length ? 'submitted' : 'new', page.threads[0]?.id);
    } catch { if (!controller.current.signal.aborted) setError(true); }
    finally { openingRef.current = false; if (!controller.current.signal.aborted) setOpening(false); }
  };
  return <SetupSupportContext.Provider value={{ openSupport: preferUnread => { void openSupport(preferUnread); }, openConversations: () => navigate('submitted') }}>
    {children ?? <SetupSupportActions />}
    <div className="fixed bottom-5 right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2 sm:right-6">
      {error && <p role="alert" className="rounded-xl bg-white p-2 text-sm sm:p-3 text-red-700 shadow-lg dark:bg-slate-900 dark:text-red-300">{t('support:loadFailed')}</p>}
      <button type="button" onClick={() => void openSupport(true)} disabled={opening}
        className="flex min-h-[48px] items-center gap-2 rounded-full bg-emerald-700 px-3 py-3 sm:px-4 text-white shadow-lg disabled:opacity-60 dark:bg-emerald-600 dark:text-white"
        aria-label={`${t('support:floatingLabel')}${unread ? ` · ${unread}` : ''}`}>
        <MessageCircle size={22} /><span>{t(opening ? 'support:opening' : 'support:floatingLabel')}</span>
        {unread > 0 && <span className="flex h-5 min-w-5 items-center sm:h-6 sm:min-w-6 justify-center rounded-full bg-white px-1 text-xs font-semibold text-emerald-800 dark:bg-slate-900 dark:text-emerald-200">{unread}</span>}
      </button>
    </div>
    {drawer && <SupportDrawer {...drawer} type="support" supportOnly setupRestaurants={restaurants} initialRestaurantId={restaurantId}
      onNavigate={navigate} onDraftChange={value => { dirty.current = value; }}
      onClose={() => { if (canLeave()) { dirty.current = false; const next = new URLSearchParams(params); next.delete('thread'); next.delete('view'); next.delete('type'); setParams(next, { replace: true }); } }} />}
  </SetupSupportContext.Provider>;
}
